import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Typography } from '@mui/material';
import api from '../../utils/api';

interface Counsellor {
  _id: string;
  firstName: string;
  lastName: string;
  status: string;
}

const OffboardingCounsellor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [counsellor, setCounsellor] = useState<Counsellor | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offboardingReason, setOffboardingReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [errors, setErrors] = useState<{ reason?: string; other?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    console.log('OffboardingCounsellor mounted with ID:', id);
    const fetchCounsellorDetails = async () => {
      try {
        setLoading(true);
        console.log('Fetching counsellor details for ID:', id);
        const response = await api.get(`/api/counsellors/${id}`);
        console.log('Received counsellor data:', response.data);
        setCounsellor(response.data);
        setError(null);
      } catch (err: any) {
        console.error('Error fetching counsellor details:', err);
        console.error('Error response:', err.response?.data);
        setError(err.response?.data?.message || 'Failed to fetch counsellor details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      console.log('Starting fetch for counsellor:', id);
      fetchCounsellorDetails();
    } else {
      console.error('No counsellor ID provided');
      setError('No counsellor ID provided');
      setLoading(false);
    }
  }, [id]);

  const validateForm = (): boolean => {
    const validationErrors: { reason?: string; other?: string } = {};

    if (!offboardingReason) {
      validationErrors.reason = 'Please select a reason for offboarding';
    }

    if (offboardingReason === 'Other' && !otherReason.trim()) {
      validationErrors.other = 'Please specify the other reason';
    }

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      const url = `/api/counsellors/${id}/offboard`;
      console.log('Sending offboard request:', {
        url,
        id,
        reason: offboardingReason === 'Other' ? otherReason : offboardingReason
      });

      await api.post(url, {
        reason: offboardingReason === 'Other' ? otherReason : offboardingReason,
      });
      
      navigate('/');
    } catch (err: any) {
      console.error('Error offboarding counsellor:', err);
      console.error('Error response:', err.response?.data);
      setError(err.response?.data?.message || 'Failed to offboard counsellor. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center bg-white p-8 rounded-lg shadow-lg">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading counsellor details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center bg-white p-8 rounded-lg shadow-lg">
          <div className="text-red-600 mb-4">{error}</div>
          <Button
            onClick={() => navigate('/')}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              minWidth: '160px',
              backgroundColor: '#2196f3',
              '&:hover': {
                backgroundColor: '#1976d2'
              }
            }}
          >
            Return Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-8 overflow-y-scroll">
      <div className="bg-white rounded-lg shadow-lg p-6 min-h-[calc(100vh-4rem)]">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="bg-white shadow">
            <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-start">
                {/* Buttons on the left */}
                <div className="flex flex-col space-y-4">
                  {/* Primary Actions */}
                  <div className="flex space-x-4">
                    <Button
                      variant="contained"
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        minWidth: '160px',
                        backgroundColor: '#2196f3',
                        '&:hover': {
                          backgroundColor: '#1976d2'
                        }
                      }}
                      onClick={() => navigate(`/counsellors/${id}`)}
                    >
                      Back to Counsellor Details
                    </Button>
                  </div>
                </div>

                {/* Title on the right */}
                <div className="text-right">
                  <Typography 
                    variant="h3" 
                    component="h1" 
                    gutterBottom
                    sx={{
                      fontFamily: 'Roboto',
                      fontWeight: 800,
                      fontSize: '2.5rem',
                      color: '#1a365d',
                      letterSpacing: '0.5px'
                    }}
                  >
                    Offboard Counsellor
                  </Typography>
                  {counsellor && (
                    <Typography 
                      variant="subtitle1" 
                      sx={{
                        color: '#4a5568',
                        fontWeight: 500,
                        fontSize: '1.1rem',
                        mb: 2
                      }}
                    >
                      {counsellor.firstName} {counsellor.lastName}
                    </Typography>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          {counsellor && (
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <div className="p-4 rounded-lg mb-6 bg-yellow-50 border border-yellow-200">
                <p className="text-sm text-yellow-800">
                  <span className="font-semibold">Note:</span> This action will mark the counsellor as inactive. 
                  Their information will be retained in the system for record-keeping purposes.
                </p>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for Offboarding:
                  </label>
                  <select
                    value={offboardingReason}
                    onChange={(e) => setOffboardingReason(e.target.value)}
                    className={`w-full p-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                      errors.reason ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select a reason</option>
                    <option value="RejectedApplication">Rejected after application</option>
                    <option value="RejectedInterview">Rejected at Interview</option>
                    <option value="WithdrewApplication">Decided to withdraw application</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.reason && (
                    <p className="mt-1 text-sm text-red-600">{errors.reason}</p>
                  )}
                </div>

                {offboardingReason === 'Other' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Please specify:
                    </label>
                    <textarea
                      value={otherReason}
                      onChange={(e) => setOtherReason(e.target.value)}
                      className={`w-full p-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                        errors.other ? 'border-red-500' : 'border-gray-300'
                      }`}
                      rows={3}
                    />
                    {errors.other && (
                      <p className="mt-1 text-sm text-red-600">{errors.other}</p>
                    )}
                  </div>
                )}

                <div className="flex justify-end mt-6">
                  <Button
                    type="submit"
                    variant="contained"
                    color="error"
                    disabled={isSubmitting}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      minWidth: '160px',
                    }}
                  >
                    {isSubmitting ? 'Processing...' : 'Offboard Counsellor'}
                  </Button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OffboardingCounsellor;